/**
 * Smart Table — Browser Printing
 * ==============================
 * Stampa diretta dal browser via WebUSB e Web Bluetooth (ESC/POS).
 * Nessun bridge necessario.
 *
 * Le device handles non sono persistenti tra sessioni: chiediamo all'utente
 * di pairare una volta sola → salviamo vendorId/productId nel record stampante,
 * al riavvio del browser ricolleghiamo dal pool dei device già autorizzati.
 */

// ---------- ESC/POS RENDERING ----------
const ESC = 0x1b;
const GS = 0x1d;

function renderJobEscPos(job) {
    const out = [];
    // init
    out.push(ESC, 0x40);
    // alignment left
    out.push(ESC, 0x61, 0x00);
    // size normal
    out.push(GS, 0x21, 0x00);

    const encoder = new TextEncoder();
    for (const line of job.lines || []) {
        const text = line.text || "";
        // alignment
        if (line.align === "center") out.push(ESC, 0x61, 0x01);
        else if (line.align === "right") out.push(ESC, 0x61, 0x02);
        else out.push(ESC, 0x61, 0x00);
        // size
        if (line.size === "large") out.push(GS, 0x21, 0x01);
        else out.push(GS, 0x21, 0x00);
        // bold
        if (line.bold) out.push(ESC, 0x45, 0x01);

        // text bytes (cp858 not available in browsers, use utf-8 fallback)
        const bytes = encoder.encode(text);
        for (const b of bytes) out.push(b);
        out.push(0x0a); // \n

        if (line.bold) out.push(ESC, 0x45, 0x00);
        out.push(GS, 0x21, 0x00);
    }
    // feed + cut
    out.push(0x0a, 0x0a, 0x0a);
    out.push(GS, 0x56, 0x00);
    return new Uint8Array(out);
}

// ---------- WEB USB ----------
export const webusb = {
    supported() {
        return typeof navigator !== "undefined" && !!navigator.usb;
    },

    /** Chiede all'utente di scegliere una stampante USB. Restituisce {vendorId, productId}. */
    async requestDevice() {
        if (!this.supported()) throw new Error("WebUSB non supportato in questo browser. Usa Chrome o Edge.");
        // Permetti tutto - alcune stampanti hanno class=0xFF (vendor specific)
        const device = await navigator.usb.requestDevice({ filters: [] });
        return {
            vendorId: device.vendorId,
            productId: device.productId,
            productName: device.productName,
            manufacturerName: device.manufacturerName,
            serialNumber: device.serialNumber,
        };
    },

    /** Trova un device già autorizzato che combaci con vendor/product ID. */
    async findPairedDevice(vendorId, productId) {
        if (!this.supported()) return null;
        const devices = await navigator.usb.getDevices();
        return devices.find(d => d.vendorId === vendorId && d.productId === productId) || null;
    },

    /** Stampa raw bytes sul device. */
    async printRaw(device, data) {
        if (!device.opened) {
            try {
                await device.open();
            } catch (e) {
                throw new Error(
                    "Impossibile aprire la stampante USB. " +
                    "Su Windows: il driver di sistema sta bloccando l'accesso. Installa WinUSB con Zadig (https://zadig.akeo.ie) — vedi guida nella pagina Stampanti. " +
                    "Dettaglio: " + e.message
                );
            }
        }
        if (device.configuration === null) await device.selectConfiguration(1);
        // Cerca interfaccia OUT bulk
        let interfaceNumber = null;
        let endpointNumber = null;
        for (const cfg of device.configurations) {
            for (const iface of cfg.interfaces) {
                for (const alt of iface.alternates) {
                    for (const ep of alt.endpoints) {
                        if (ep.direction === "out" && ep.type === "bulk") {
                            interfaceNumber = iface.interfaceNumber;
                            endpointNumber = ep.endpointNumber;
                        }
                    }
                }
            }
        }
        if (interfaceNumber === null) throw new Error("Nessuna interfaccia USB OUT bulk trovata sul device.");
        try {
            await device.claimInterface(interfaceNumber);
        } catch (e) {
            throw new Error(
                "Impossibile prendere il controllo della stampante USB. " +
                "Causa più comune: su Windows il driver di stampa fiscale ha già 'claimato' il dispositivo. " +
                "Soluzione: installa il driver WinUSB con Zadig (https://zadig.akeo.ie) → Options → List All Devices → seleziona la stampante → WinUSB → Replace Driver. " +
                "Dopo Zadig la stampante non stampa più PDF/Word ma funziona perfettamente con Smart Table. " +
                "Dettaglio: " + e.message
            );
        }
        try {
            await device.transferOut(endpointNumber, data);
        } finally {
            try { await device.releaseInterface(interfaceNumber); } catch { /* */ }
        }
    },

    /** API alto livello: stampa un job sulla stampante USB con (vendorId, productId). */
    async printJob(job, vendorId, productId) {
        const device = await this.findPairedDevice(vendorId, productId);
        if (!device) throw new Error("Stampante USB non collegata o non autorizzata in questo browser.");
        const bytes = renderJobEscPos(job);
        await this.printRaw(device, bytes);
    },
};

// ---------- WEB BLUETOOTH ----------
export const webbt = {
    supported() {
        return typeof navigator !== "undefined" && !!navigator.bluetooth;
    },

    async requestDevice() {
        if (!this.supported()) throw new Error("Web Bluetooth non supportato. Usa Chrome o Edge.");
        // Filtro stampanti generiche: Serial Port Profile (SPP) UUID standard + ESC/POS noti
        const device = await navigator.bluetooth.requestDevice({
            acceptAllDevices: true,
            optionalServices: [
                "000018f0-0000-1000-8000-00805f9b34fb", // ESC/POS Printer Service common
                "00001101-0000-1000-8000-00805f9b34fb", // SPP
                "e7810a71-73ae-499d-8c15-faa9aef0c3f2", // some chinese printers
            ],
        });
        return {
            id: device.id,        // persistent device id (browser-specific)
            name: device.name || "",
        };
    },

    async findPairedDevice(deviceId) {
        if (!this.supported() || !navigator.bluetooth.getDevices) return null;
        // getDevices() richiede permission policy; alcuni browser non lo supportano
        try {
            const devices = await navigator.bluetooth.getDevices();
            return devices.find(d => d.id === deviceId) || null;
        } catch { return null; }
    },

    async printJob(job, deviceId) {
        const device = await this.findPairedDevice(deviceId);
        if (!device) throw new Error("Stampante Bluetooth non collegata o non autorizzata in questo browser.");
        const server = await device.gatt.connect();
        // Tentativo: trova service ESC/POS
        const services = await server.getPrimaryServices();
        let char = null;
        for (const svc of services) {
            const chars = await svc.getCharacteristics();
            char = chars.find(c => c.properties.write || c.properties.writeWithoutResponse);
            if (char) break;
        }
        if (!char) {
            server.disconnect();
            throw new Error("Nessuna caratteristica Bluetooth writable trovata.");
        }
        const bytes = renderJobEscPos(job);
        // Chunked write (max 512 bytes per packet su molti device)
        const chunkSize = 200;
        for (let i = 0; i < bytes.length; i += chunkSize) {
            const chunk = bytes.slice(i, i + chunkSize);
            if (char.properties.writeWithoutResponse) {
                await char.writeValueWithoutResponse(chunk);
            } else {
                await char.writeValue(chunk);
            }
        }
        server.disconnect();
    },
};

export { renderJobEscPos };
